import { useEffect, useId, useMemo, useRef, useState } from 'react';

interface TagSelectProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  loading?: boolean;
  // When false, the selected-tag chips are not rendered inline (the caller shows
  // them elsewhere) — keeps the control a fixed height inside a toolbar row.
  showChips?: boolean;
}

export function TagSelect({
  options,
  value,
  onChange,
  placeholder = 'Add tags…',
  loading = false,
  showChips = true,
}: TagSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return options
      .filter((o) => !value.includes(o))
      .filter((o) => !q || o.toLowerCase().includes(q));
  }, [options, value, search]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const add = (tag: string) => {
    onChange([...value, tag]);
    setSearch('');
  };
  const remove = (tag: string) => onChange(value.filter((t) => t !== tag));

  const hasOptions = options.length > 0;

  return (
    <div className={`searchable-select${open ? ' open' : ''}`} ref={rootRef}>
      {showChips && value.length > 0 && (
        <div className="tag-chips tag-select-chips">
          {value.map((tag) => (
            <span key={tag} className="chip">
              {tag}
              <button
                type="button"
                className="chip-remove"
                aria-label={`Remove ${tag}`}
                onClick={() => remove(tag)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <button
        type="button"
        className="searchable-select-trigger"
        disabled={loading || !hasOptions}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          if (loading || !hasOptions) return;
          setOpen((v) => !v);
          if (open) setSearch('');
        }}
      >
        <span className="muted">
          {loading ? 'Loading…' : !hasOptions ? 'No tags available' : placeholder}
        </span>
        <span className="searchable-select-chevron" aria-hidden>▾</span>
      </button>
      {open && (
        <div className="searchable-select-menu" role="listbox" id={listId}>
          <div className="searchable-select-search">
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags…"
              aria-label="Search tags"
            />
          </div>
          <ul className="searchable-select-options">
            {available.length === 0 && (
              <li className="searchable-select-empty">No matching tags</li>
            )}
            {available.map((option) => (
              <li key={option}>
                <button type="button" role="option" aria-selected={false} onClick={() => add(option)}>
                  {option}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
